import React, { Suspense, useEffect } from 'react'

import PropTypes from 'prop-types'
import { Navigate, useLocation } from 'react-router-dom'

import {
  SERVICES_PATH,
  CATALOG_PATH,
  WORKSHOP_EVENTS_PATH,
  MECHANIC_EVENTS_PATH,
} from '@/utils/routePaths'

import ErrorHandler from '@/components/ErrorHandler'

import LottieLoader from '@/ui/LottieLoader'

import { useUser } from '@/blocks/Auth/contexts/AuthContext'
import { useAuthActions } from '@/blocks/Auth/hooks/useAuthActions'
import { useAuthDataHandler } from '@/blocks/Auth/hooks/useAuthDataHandler'

const SERVICE_PATH_MAP = {
  [SERVICES_PATH]: 'files',
  [CATALOG_PATH]: 'catalog',
  [WORKSHOP_EVENTS_PATH]: 'workShop',
  [MECHANIC_EVENTS_PATH]: 'workShop',
}

const pathRequiresService = (pathname) => {
  const matchedPath = Object.keys(SERVICE_PATH_MAP).find((path) =>
    pathname.startsWith(path),
  )
  return matchedPath ? SERVICE_PATH_MAP[matchedPath] : false
}

const Preloader = () => (
  <LottieLoader variant='center' size='8rem' height='100vh' width='100%' />
)

export default function RouteComponent({
  Layout,
  layoutProps,
  Component,
  isPrivate = false,
  isDocTokenPage = false,
}) {
  const location = useLocation()
  const { isLoggedIn, isLoading, isDocToken: isDocTokenSession } = useUser()
  const { getLoginSessionData, getDefaultHomePage } = useAuthDataHandler()
  const { handleLogout } = useAuthActions()

  const { services: enabledServices } = getLoginSessionData() ?? {}
  const requiredService = pathRequiresService(location.pathname)

  const isUnauthorizedUser = !isLoggedIn && isPrivate
  const isUnauthorizedDocTokenUser = isDocTokenSession && !isDocTokenPage
  const hasNoServices = isLoggedIn && !isDocTokenSession && !enabledServices

  const shouldLogout =
    isUnauthorizedUser || isUnauthorizedDocTokenUser || hasNoServices

  useEffect(() => {
    if (shouldLogout) {
      handleLogout()
    }
  }, [shouldLogout])

  if (!Component || shouldLogout) {
    return null
  }

  const hasServiceAccess =
    requiredService && !enabledServices?.[requiredService] && !isDocTokenSession

  if (hasServiceAccess) {
    const defaultHome = getDefaultHomePage(enabledServices)
    return <Navigate to={defaultHome} replace />
  }

  const ComponentBlock = isLoading && isPrivate ? Preloader : Component

  const WrappedComponent = (
    <ErrorHandler>
      <ComponentBlock />
    </ErrorHandler>
  )

  return (
    <Suspense fallback={<Preloader />}>
      {Layout ? (
        <Layout {...layoutProps}>{WrappedComponent}</Layout>
      ) : (
        WrappedComponent
      )}
    </Suspense>
  )
}

RouteComponent.propTypes = {
  Layout: PropTypes.elementType,
  Component: PropTypes.elementType,
  isPrivate: PropTypes.bool,
  layoutProps: PropTypes.object,
  isDocTokenPage: PropTypes.bool,
}
